import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import DataTable from '../components/Common/DataTable';
import SearchBar from '../components/Common/SearchBar';
import StatusBadge from '../components/Common/StatusBadge';
import Pagination from '../components/Common/Pagination';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import './CaseList.css';

export default function CaseList() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status') || '';
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [universityFilter, setUniversityFilter] = useState('');
  const [universities, setUniversities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const newStatus = searchParams.get('status') || '';
    setStatusFilter(newStatus);
  }, [searchParams]);

  useEffect(() => {
    fetchUniversities();
  }, []);

  useEffect(() => {
    fetchCases();
  }, [search, statusFilter, universityFilter, currentPage]);

  const fetchUniversities = async () => {
    try {
      const data = await api.universities.list();
      setUniversities(Array.isArray(data) ? data : (data.universities || data.data || []));
    } catch (error) {
      console.error('Failed to fetch universities:', error);
    }
  };

  const fetchCases = async () => {
    try {
      setLoading(true);
      const params = {
        search,
        status: statusFilter,
        university_id: universityFilter,
        page: currentPage,
        limit: itemsPerPage,
      };
      const data = await api.cases.list(params);
      const result = data.data || data;
        setCases(Array.isArray(result) ? result : (result.cases || []));
        if (result.pagination) { setTotalPages(result.pagination.pages || 1); }
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      toast.error('Failed to load cases');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCase = async (e, caseId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this case? This action cannot be undone.')) return;
    try {
      await api.cases.delete(caseId);
      toast.success('Case deleted successfully');
      setCases(cases.filter(c => c.id !== caseId));
    } catch (error) {
      toast.error('Failed to delete case');
      console.error(error);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      'Case ID',
      'Applicant',
      'University',
      'Course',
      'Apply Date',
      'Status',
    ];
    const rows = cases.map((c) => [
      c.case_number,
      c.applicant_name,
      c.university_name,
      c.course_name,
      formatDate(c.apply_date),
      c.status,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cases-${new Date().getTime()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { key: 'case_number', label: 'Case ID', sortable: true },
    { key: 'applicant_name', label: 'Applicant', sortable: true },
    { key: 'university_name', label: 'University', sortable: true },
    { key: 'course_name', label: 'Course', sortable: true },
    { key: 'register_roll_no', label: 'Register No', sortable: false },
    { key: 'apply_for', label: 'Apply For', sortable: true },
    {
      key: 'apply_date',
      label: 'Apply Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: 'expected_date',
      label: 'Expected Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    ...(isAdmin ? [{
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (value, row) => (
        <button
          onClick={(e) => handleDeleteCase(e, row.id)}
          className="btn-icon btn-danger-icon"
          title="Delete case"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', padding: '4px' }}
        >
          <Trash2 size={16} />
        </button>
      ),
    }] : []),
  ];

  return (
    <div className="case-list-page">
      <div className="page-header">
        <div>
          <h1>Cases</h1>
          <p className="text-gray">Manage all transcript verification cases</p>
        </div>
        {isAdmin && (
          <button
            className="btn btn-primary"
            onClick={() => navigate('/cases/new')}
          >
            <Plus size={18} />
            New Case
          </button>
        )}
      </div>

      <div className="case-list-container">
        <div className="filters-row">
          <div className="search-column">
            <SearchBar
              placeholder="Search cases, applicants, or universities..."
              value={search}
              onSearch={setSearch}
            />
          </div>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="documents_uploaded">Documents Uploaded</option>
            <option value="in_process">In Process</option>
            <option value="verification_started">Verification Started</option>
            <option value="pending_clarification">Pending Clarification</option>
            <option value="completed">Completed</option>
            <option value="closed">Closed</option>
          </select>

          {isAdmin && (
            <select
              className="filter-select"
              value={universityFilter}
              onChange={(e) => {
                setUniversityFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Universities</option>
              {universities.map((uni) => (
                <option key={uni.id} value={uni.id}>
                  {uni.name}
                </option>
              ))}
            </select>
          )}

          <button
            className="btn btn-secondary btn-small"
            onClick={handleExportCSV}
          >
            Export CSV
          </button>
        </div>

        <DataTable
          columns={columns}
          data={cases}
          loading={loading}
          onRowClick={(row) => navigate(`/cases/${row.id}`)}
          emptyMessage="No cases found"
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={cases.length}
          />
        )}
      </div>
    </div>
  );
}
